import React, { useContext, useEffect, useState } from 'react';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Alert from 'Components/DesignSystem/Alert/Alert';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import Tabs from './Tabs';
import SearchBar from './SearchBar';
import FilterButton from './FilterButton';
import TopItemsSelector from './TopItemsSelector';
import SettingsButton from './SettingsButton';
import AnalysisButton from './AnalysisButton';
import AppliedFilters from './AppliedFilters';
import CategoryRelevancyTable from './CategoryRelevancyTable';
import Top25Table from './Top25Table';
import ManageUnifiedScoringPanel from './ManageUnifiedScoringPanel';
import RuleView from 'Pages/GMS/Rules/RuleView';
import RulesAddEditManage from 'Components/GMS/Rules/RulesAddEditManage';
import { RulesContextProvider } from 'Components/GMS/Rules/RulesContext';
import { ArsContextProvider } from 'Pages/GMS/ARS/Contexts/ArsContext';
import { RuleContextProvider } from 'Pages/GMS/Rules/contexts/RuleContext';
import { CategoryRelevancyContext } from '../contexts/CategoryRelevancyContext';
import { AppContext } from 'Context/AppContext';
import { useMenuControl } from '../hooks/useMenuControl';
import { CATEGORY_RELEVANCY, COMPUTE, COMPUTING_IN_PROGRESS, ESTIMATED_COMPLETION, LAST_UPDATED_ON } from '../constants/CategoryRelevancyConstants';
import { Navigate } from 'react-router';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { formatDateDDMMMYYYY } from 'Common/HelperClass';

const CategoryRelevancy: React.FC = () => {
    const {
        activeTab,
        masterCategories,
        masterCategoryTypes,
        rowDensity,
        openMenu,

        rawCategories,
        readPermission,
        rawExpandedCategories,
        rawCoSoldCategories,
        rawFilters,
        rawSearchTerm,
        rawSearchPending,
        rawSortBy,
        rawSortOrder,
        rawLoadingCategories,
        isRawCoSoldCategoryLoading: isRawCategoryLoading,
        handleRawCategoryExpand,
        handleRawSearch,
        handleRawFilterChange,
        handleRawRemoveFilter,
        handleRawSort,

        curatedCategories,
        curatedExpandedCategories,
        curatedCoSoldCategories,
        curatedFilters,
        curatedSearchTerm,
        curatedSearchPending,
        curatedSortBy,
        curatedSortOrder,
        curatedLoadingCategories,
        isCuratedCoSoldCategoryLoading: isCuratedCategoryLoading,
        handleCuratedCategoryExpand,
        handleCuratedSearch,
        handleCuratedFilterChange,
        handleCuratedRemoveFilter,
        handleCuratedSort,

        top25Rows,
        top25Loading,
        top25SortBy,
        top25SortOrder,
        top25SearchTerm,
        top25SearchPending,
        top25Filters,
        handleTop25Search,
        handleTop25FilterChange,
        handleTop25RemoveFilter,
        top25SelectedAnalyses,
        handleTop25AnalysisChange,
        handleTop25Sort,

        setActiveTab,
        handleToggleCuration,
        handleTopItemsChange,
        handleRowDensityChange,

        topItems,
        customTopItems,
        lastUpdatedOn,
        handleUpdateReviewStatus,
        selectedAnalyses,
        handleAnalysisChange,
        isComputing,
        computeStartedAt,
        handleComputeTrigger,
        notifyWeightageRescoreTriggered,
    } = useContext(CategoryRelevancyContext);

    const appContext = useContext(AppContext);
    const channelKey = appContext?.channelKey;

    const { toggleMenu, filterRef, topItemsRef, settingsRef, analysisRef } = useMenuControl();
    const [isUnifiedScoringPanelOpen, setIsUnifiedScoringPanelOpen] = useState(false);

    // Force re-render every 60s while computing so the countdown IIFE recalculates
    const [, setCountdownTick] = useState(0);
    useEffect(() => {
        if (!isComputing) return;
        const id = setInterval(() => setCountdownTick(t => t + 1), 60_000);
        return () => clearInterval(id);
    }, [isComputing]);

    type CompatibilityPanelState = {
        mode: 'create' | 'view';
        categoryA: { id: string; name: string };
        categoryB: { id: string; name: string };
        ruleId?: string;
    };
    const [compatibilityPanel, setCompatibilityPanel] = useState<CompatibilityPanelState | null>(null);

    const handleOpenCreateCompatibility = (categoryA: { id: string; name: string }, categoryB: { id: string; name: string }) => {
        setCompatibilityPanel({ mode: 'create', categoryA, categoryB, ruleId: '0' });
    };
    const handleOpenViewCompatibility = (categoryA: { id: string; name: string }, categoryB: { id: string; name: string }) => {
        setCompatibilityPanel({ mode: 'view', categoryA, categoryB });
    };

    if (!readPermission) {
        return <Navigate to="/workspace/module/permission/denied" replace />;
    }

    const handleCompute = async () => {
        try {
            await handleComputeTrigger();
            toast.success('Compute triggered successfully');
        } catch {
            toast.error('Failed to trigger compute');
        }
    };

    // Determine active analysis state — Top 25 and All share the same selection state.
    const activeAnalyses = activeTab === 'top25' ? top25SelectedAnalyses : selectedAnalyses;

    // Unified pairs are always included in the BE filter (selecting Order Data
    // or Taxonomy still pulls in unified rows via `unified,order_data` /
    // `unified,taxonomy`), so both metric columns are always meaningful.
    const showAttributeOverlap = true;
    const showUnifiedScoring = true;

    const currentCategories = activeTab === 'all' ? rawCategories : curatedCategories;
    const currentExpandedCategories = activeTab === 'all' ? rawExpandedCategories : curatedExpandedCategories;
    const currentCoSoldCategories = activeTab === 'all' ? rawCoSoldCategories : curatedCoSoldCategories;
    const currentFilters = activeTab === 'curated' ? curatedFilters : activeTab === 'top25' ? top25Filters : rawFilters;
    const currentSearchTerm = activeTab === 'curated' ? curatedSearchTerm : rawSearchTerm;
    /* § 288 — the same tab ternary as the term it belongs to. */
    const currentSearchPending = activeTab === 'curated' ? curatedSearchPending : rawSearchPending;
    const currentSortBy = activeTab === 'all' ? rawSortBy : curatedSortBy;
    const currentSortOrder = activeTab === 'all' ? rawSortOrder : curatedSortOrder;
    const currentLoadingCategories = activeTab === 'all' ? rawLoadingCategories : curatedLoadingCategories;
    const currentLoadingCoSoldCategories = activeTab === 'all' ? isRawCategoryLoading : isCuratedCategoryLoading;
    const handleCategoryExpand = activeTab === 'all' ? handleRawCategoryExpand : handleCuratedCategoryExpand;
    const handleSearch = activeTab === 'curated' ? handleCuratedSearch : handleRawSearch;
    const handleFilterChange = activeTab === 'curated' ? handleCuratedFilterChange : activeTab === 'top25' ? handleTop25FilterChange : handleRawFilterChange;
    const handleRemoveFilter = activeTab === 'curated' ? handleCuratedRemoveFilter : activeTab === 'top25' ? handleTop25RemoveFilter : handleRawRemoveFilter;
    const handleSort = activeTab === 'all' ? handleRawSort : handleCuratedSort;

    return (
        <section aria-labelledby="category-relevancy-title">
            {channelKey && (
                <ManageUnifiedScoringPanel
                    isOpen={isUnifiedScoringPanelOpen}
                    onClose={() => setIsUnifiedScoringPanelOpen(false)}
                    channelKey={channelKey}
                    onWeightageSaved={notifyWeightageRescoreTriggered}
                />
            )}
            {/* RuleView and RulesAddEditManage (via RuleHeader) read ArsContext/RuleContext directly —
                this route isn't one of the 7 wrapped in RECOMMENDER_PROVIDERS in App.tsx, so both
                providers have to be supplied locally here. See okf/category-relevancy/e2e.md § 7. */}
            {(compatibilityPanel?.mode === 'view' || compatibilityPanel?.mode === 'create') && (
                <ArsContextProvider>
                    <RuleContextProvider>
                        {compatibilityPanel?.mode === 'view' && (
                            <RuleView
                                mode="category_relevancy"
                                baseCategories={[compatibilityPanel.categoryA.id]}
                                targetCategories={[compatibilityPanel.categoryB.id]}
                                onClose={() => setCompatibilityPanel(null)}
                                onOpenCreateCompatibility={() => setCompatibilityPanel({ ...compatibilityPanel, mode: 'create' })}
                            />
                        )}
                        {compatibilityPanel?.mode === 'create' && (
                            <RulesContextProvider key={compatibilityPanel.ruleId ?? '0'}>
                                <RulesAddEditManage
                                    ruleId={compatibilityPanel.ruleId ?? '0'}
                                    type="edit"
                                    fromCategoryRelevancy={true}
                                    initialCategoryA={compatibilityPanel.categoryA}
                                    initialCategoryB={compatibilityPanel.categoryB}
                                    onClose={() => setCompatibilityPanel(null)}
                                    onSaved={(newId: string) => setCompatibilityPanel(prev => prev ? { ...prev, ruleId: newId } : null)}
                                />
                            </RulesContextProvider>
                        )}
                    </RuleContextProvider>
                </ArsContextProvider>
            )}
            <div className="tw-flex tw-items-center tw-justify-between tw-mt-6 tw-mb-4">
                <Typography variant="display" as="h1" id="category-relevancy-title" className="tw-text-ink">
                    {CATEGORY_RELEVANCY}
                </Typography>
                <div className="tw-flex tw-items-center tw-gap-4">
                    <span className="tw-text-sm tw-text-ink-secondary">
                        {LAST_UPDATED_ON}: <span className="tw-font-semibold tw-text-ink">
                            {lastUpdatedOn ? formatDateDDMMMYYYY(lastUpdatedOn) : '-'}
                        </span>
                    </span>
                    <Button
                        data-testid="category-relevancy-compute-btn"
                        variant="primary"
                        onClick={handleCompute}
                        disabled={isComputing}
                    >
                        {isComputing ? `${COMPUTE}…` : COMPUTE}
                    </Button>
                </div>
            </div>

            <div className="tw-mt-2 tw-box-border tw-w-auto tw-border-t-[1px] tw-border-solid tw-border-edge" />

            {isComputing && (
                <Alert tone="warning" live className="tw-mt-3">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Spinner size="sm" label="Computing" />
                        <span className="tw-font-semibold tw-text-ink">{COMPUTING_IN_PROGRESS}</span>
                        <span aria-hidden="true" className="tw-text-ink-tertiary">|</span>
                        <span>{ESTIMATED_COMPLETION} {(() => {
                        const ESTIMATED_MS = 10 * 60 * 1000; // 10 minutes — current ars_automation_svc compute typically finishes well under this
                        const elapsed = computeStartedAt ? Date.now() - computeStartedAt : 0;
                        const remaining = Math.max(0, ESTIMATED_MS - elapsed);
                        const hrs = Math.floor(remaining / 3600000);
                        const mins = Math.floor((remaining % 3600000) / 60000);
                        const minsLabel = `${mins} ${mins === 1 ? 'minute' : 'minutes'}`;
                        if (hrs > 0) {
                            const hrsLabel = `${hrs} ${hrs === 1 ? 'hour' : 'hours'}`;
                            return `${hrsLabel} ${minsLabel}`;
                        }
                        return minsLabel;
                    })()}</span>
                    </div>
                </Alert>
            )}

            <div className={`tw-mb-6 ${isComputing ? 'tw-pointer-events-none tw-opacity-60' : ''}`}>
                <Tabs activeTab={activeTab} onTabChange={setActiveTab} />
            </div>

            <div className={`tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-mb-4 tw-gap-y-3 ${isComputing ? 'tw-pointer-events-none tw-opacity-60' : ''}`}>
                {/* § 288 — the fetch flag for the live tab, plus the 500ms debounce in
                    front of it. Top 25 is split out because `currentLoadingCategories`
                    falls through to the CURATED flag on that tab. */}
                <SearchBar
                    value={activeTab === 'top25' ? top25SearchTerm : currentSearchTerm}
                    onChange={activeTab === 'top25' ? handleTop25Search : handleSearch}
                    loading={activeTab === 'top25'
                        ? top25Loading || top25SearchPending
                        : currentLoadingCategories || currentSearchPending}
                    loadingLabel="Searching categories"
                />

                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <div ref={filterRef}>
                        <FilterButton
                            filters={currentFilters}
                            onFilterChange={handleFilterChange}
                            categories={masterCategories}
                            categoryTypes={masterCategoryTypes}
                            isOpen={openMenu === 'filter'}
                            onToggle={() => toggleMenu('filter')}
                            activeTab={activeTab}
                        />
                    </div>

                    {activeTab === 'all' && (
                        <div ref={topItemsRef}>
                            <TopItemsSelector
                                value={topItems}
                                customValue={customTopItems}
                                onChange={handleTopItemsChange}
                                disabled={isComputing}
                                isOpen={openMenu === 'topItems'}
                                onToggle={() => toggleMenu('topItems')}
                            />
                        </div>
                    )}

                    <div ref={settingsRef}>
                        <SettingsButton
                            onRowDensityChange={handleRowDensityChange}
                            isOpen={openMenu === 'settings'}
                            onToggle={() => toggleMenu('settings')}
                            currentDensity={rowDensity}
                        />
                    </div>

                    {activeTab !== 'curated' && (
                        <div ref={analysisRef}>
                            <AnalysisButton
                                selectedAnalyses={activeAnalyses}
                                onAnalysisChange={activeTab === 'top25' ? handleTop25AnalysisChange : handleAnalysisChange}
                                isOpen={openMenu === 'analysis'}
                                onToggle={() => toggleMenu('analysis')}
                                onManageUnifiedScoring={() => setIsUnifiedScoringPanelOpen(true)}
                            />
                        </div>
                    )}
                </div>
            </div>

            {activeTab !== 'curated' && (
                <AppliedFilters
                    filters={currentFilters}
                    onRemoveFilter={handleRemoveFilter}
                    onFilterChange={handleFilterChange}
                />
            )}

            {activeTab === 'top25' ? (
                <Top25Table
                    rows={top25Rows}
                    loading={top25Loading}
                    rowDensity={rowDensity}
                    showAttributeOverlap={showAttributeOverlap}
                    showUnifiedScoring={showUnifiedScoring}
                    showTaxonomyIcon={true}
                    onToggleCuration={handleToggleCuration}
                    onUpdateReviewStatus={handleUpdateReviewStatus}
                    handleSort={handleTop25Sort}
                    sortBy={top25SortBy}
                    sortOrder={top25SortOrder}
                    disabled={isComputing}
                    onOpenCreateCompatibility={handleOpenCreateCompatibility}
                    onOpenViewCompatibility={handleOpenViewCompatibility}
                />
            ) : (
                <CategoryRelevancyTable
                    categories={currentCategories}
                    expandedCategories={currentExpandedCategories}
                    coSoldCategories={currentCoSoldCategories}
                    onCategoryExpand={isComputing ? () => { } : handleCategoryExpand}
                    onToggleCuration={handleToggleCuration}
                    onUpdateReviewStatus={handleUpdateReviewStatus}
                    isCuratedTab={activeTab === 'curated'}
                    rowDensity={rowDensity}
                    loadingCategories={currentLoadingCategories}
                    loadingCoSoldCategories={currentLoadingCoSoldCategories}
                    handleSort={handleSort}
                    sortBy={currentSortBy}
                    sortOrder={currentSortOrder}
                    showAttributeOverlap={showAttributeOverlap}
                    showUnifiedScoring={showUnifiedScoring}
                    showTaxonomyIcon={true}
                    disabled={isComputing}
                    onOpenCreateCompatibility={handleOpenCreateCompatibility}
                    onOpenViewCompatibility={handleOpenViewCompatibility}
                />
            )}
        </section>
    );
};

export default CategoryRelevancy;
